import { DeviceType } from '@/__generated__/graphql';
import { getDeviceTypeAttributes } from '@/utils/attributes/deviceType';

type SensorWithLabel = [number, string];

type ChildDevice = {
  sensorName: string;
  sensorVersion?: string;
  mappers: SensorWithLabel[];
};

const sensorsWithLabel: SensorWithLabel[] = [
  [6, 'Sensor de nivel'],
  [7, 'Inclinómetro'],
  [11, 'Sensor de nivel'], //doppler
  [12, 'Sensor de flujo'],
  [13, 'Sensor de volumen'],
  [14, 'Sensor de nivel'], // radar
  [15, 'Sensor de velocidad'], // radar
  [16, 'Potenciómetro'],
  [17, 'Sensor de velocidad'], //doppler
];

const getSensors = (...ids: number[]) => {
  return ids.map((id) => {
    const sensor = sensorsWithLabel.find(([sensorId]) => sensorId === id);
    if (!sensor) throw new Error('Sensor not in sensors list');
    return sensor;
  });
};

const allowedChildren: ChildDevice[] = [
  {
    sensorName: 'L',
    mappers: getSensors(6),
  },
  {
    sensorName: 'F',
    mappers: getSensors(12, 13),
  },
  {
    sensorName: 'LV',
    mappers: getSensors(14, 15),
  },
  {
    sensorName: 'LV',
    sensorVersion: 'D1',
    mappers: getSensors(11, 17),
  },
  {
    sensorName: 'LF',
    mappers: getSensors(6, 12, 13),
  },
  {
    sensorName: 'G',
    sensorVersion: 'I',
    mappers: getSensors(7),
  },
  {
    sensorName: 'G',
    sensorVersion: 'P',
    mappers: getSensors(16),
  },
];

const getAllowedChildrenBySensorName = (...names: string[]) => {
  return names.map((sensorName) => {
    const [name, version] = sensorName.split('/');
    const sensorGroup = allowedChildren.find((sensor) => {
      const _version = version || undefined;
      return sensor.sensorName === name && _version === sensor.sensorVersion;
    });
    if (!sensorGroup)
      throw new Error(`Sensor not in sensors list: ${name} / ${version} , sameVersion`);
    return sensorGroup;
  });
};

const nonIntegrationTypes: (
  | DeviceType
  | [DeviceType, 'D1' | 'Vol' | 'P' | null, ChildDevice[] | null]
)[] = [
  DeviceType.TNanoL,
  [DeviceType.T_5, null, getAllowedChildrenBySensorName('F', 'L', 'LV', 'LV/D1')],
  DeviceType.T_5L,
  [DeviceType.T_5Lg, null, getAllowedChildrenBySensorName('L', 'G/I', 'G/P')],
  DeviceType.T_5Lv,
  [DeviceType.T_5Lv, 'D1', null],
  [DeviceType.TMicro, null, getAllowedChildrenBySensorName('F', 'L', 'LV', 'LV/D1')],
  DeviceType.TMicroL,
  DeviceType.TMicroLv,
  [DeviceType.TMicroLv, 'D1', null],
  [DeviceType.TMicroLg, null, getAllowedChildrenBySensorName('L', 'G/I', 'G/P')],
  [DeviceType.TCore, null, getAllowedChildrenBySensorName('F', 'L', 'LV', 'LV/D1')],
  DeviceType.TCoreL,
  DeviceType.TCoreLv,
  [DeviceType.TCoreLv, 'D1', null],
  [DeviceType.TCoreLg, null, getAllowedChildrenBySensorName('L', 'G/I', 'G/P')],
  DeviceType.TCoreLf,
  DeviceType.TCoreF,
  DeviceType.Regis,
];

const integrationTypes: (DeviceType | [DeviceType, 'D1' | 'Vol' | 'P'])[] = [
  DeviceType.IL,
  DeviceType.IF,
  [DeviceType.IL, 'Vol'],
];

type TypeOption = {
  enumValue: DeviceType;
  label: string;
  sensorVersion?: string;
  allowedChildren?: ChildDevice[];
};

export const typeOptions: TypeOption[] = nonIntegrationTypes.map((type) => {
  if (Array.isArray(type)) {
    const { value, label } = getDeviceTypeAttributes(type[0]);
    const sensorVersion = type[1] ?? undefined;
    const labelWithVersion = sensorVersion ? `${label}/${sensorVersion}` : label;
    return {
      enumValue: value,
      label: labelWithVersion,
      sensorVersion,
      allowedChildren: type[2] ?? undefined,
    };
  } else {
    const { value, label } = getDeviceTypeAttributes(type);
    return {
      enumValue: value,
      label,
    };
  }
});

export const integrationTypeOptions: TypeOption[] = integrationTypes.map((type) => {
  if (Array.isArray(type)) {
    const { value, label } = getDeviceTypeAttributes(type[0]);
    const sensorVersion = type[1] ?? undefined;
    const labelWithVersion = sensorVersion ? `${label}/${sensorVersion}` : label;
    return {
      enumValue: value,
      label: labelWithVersion,
      sensorVersion,
    };
  } else {
    const { value, label } = getDeviceTypeAttributes(type);
    return {
      enumValue: value,
      label,
    };
  }
});

const allTypes = [...typeOptions, ...integrationTypeOptions];

export const hasSensors = (typeLabel: string) => {
  return allTypes.some((type) => type.label === typeLabel && type.allowedChildren?.length);
};

export const getAllowedChildren = (typeLabel: string) => {
  const type = allTypes.find((type) => type.label === typeLabel);
  if (type) {
    const { allowedChildren } = type;
    return allowedChildren ?? [];
  } else {
    return [];
  }
};

export const getSensorLabel = (sensorName: string, sensorVersion?: string) => {
  let label = sensorName;
  if (sensorVersion) label += `/${sensorVersion}`;
  return label;
};

export const getSensorTypeLabel = (typeId: number) => {
  const sensor = sensorsWithLabel.find((sensor) => sensor[0] === typeId);
  return sensor?.[1] ?? '';
};

export const getTypeOption = (typeLabel: string) => {
  return allTypes.find((type) => type.label === typeLabel);
};

export const isIntegrationType = (typeLabel: string) => {
  return integrationTypeOptions.some((option) => option.label === typeLabel);
};

export const isNonIntegrationType = (typeLabel: string) => {
  return typeOptions.some((option) => option.label === typeLabel);
};
